import { useRef, useState } from "react";
import { EditorRefApi } from "shared/components/Editor";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { NoteData } from "../types";
import { extractEntitiesFromJson, isTextEmpty } from "../helpers";
import { cx } from "shared/utils/classnames";

interface NoteFormProps {
  onSubmit: (data: NoteData) => Promise<void>;
  onCancel?: () => void;
  initialValue?: string;
  applicationId?: string;
  propertyName?: string;
  isLoading?: boolean;
  editorContainerClassName?: string;
}

export const NoteForm = ({
  onSubmit,
  onCancel,
  initialValue,
  applicationId,
  propertyName,
  isLoading = false,
  editorContainerClassName
}: NoteFormProps) => {
  const editorRef = useRef<EditorRefApi>(null);
  const [isEmpty, setIsEmpty] = useState(() => isTextEmpty(initialValue));

  return (
    <div className="space-y-3">
      <LiteTextEditor
        applicationId={applicationId}
        propertyName={propertyName}
        ref={editorRef}
        id="note-editor"
        editable
        initialValue={initialValue}
        placeholder="You can write a note here"
        containerClassName={cx(
          "min-h-36 border border-alpha-100 py-3 px-4 rounded-lg text-base shadow-xs font-medium",
          editorContainerClassName
        )}
        onChange={() => {
          const doc = editorRef.current?.getDocument();
          if (doc) setIsEmpty(isTextEmpty(doc.text));
        }}
      />

      <Show if={!isEmpty}>
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => {
              onCancel?.();
              editorRef.current?.clearEditor();
            }}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            disabled={isLoading}
            loading={isLoading}
            onClick={async () => {
              const doc = editorRef.current?.getDocument();
              if (!doc) return;

              const entities = extractEntitiesFromJson(doc.json);

              const value: NoteData = {
                message: doc.text,
                document_html: doc.html,
                entities
              };

              await onSubmit(value);
              editorRef.current?.clearEditor();
              setIsEmpty(true);
            }}
          >
            Add Note
          </Button>
        </div>
      </Show>
    </div>
  );
};
